import { composeFilters } from "@/shared/components/ResourceView/Filters/composeFilters";
import type { Filter } from "@/shared/components/ResourceView/Filters/FilterSchema";
import {
  ACTIVITY_WINDOW_MAX_MS,
  type ResolvedWindow,
  type TimeWindow,
} from "@/shared/domain/actions";
import type { Condition } from "@/types";

const CREATED = "created";

/**
 * The activity panel's window, from the table's "created" filter.
 *
 * Keyed on the filter itself rather than on the bounds it expands to, so "last 24 hours" is one
 * window however many times it is resolved. The bounds: both from a BETWEEN; the missing one from
 * now, or from as far back as the activity API allows, otherwise; that whole reach with no filter
 * at all. A filter reaching further back than the API allows is cut there and says so.
 */
export function activityWindowFor(filter: Filter | undefined): TimeWindow {
  return {
    key: filter ?? null,
    resolve: (now) => resolveCreatedWindow(filter, now),
  };
}

function resolveCreatedWindow(
  filter: Filter | undefined,
  now: Date,
): ResolvedWindow {
  const bounds = filter
    ? createdBounds(composeFilters([], [filter], "AND"))
    : {};
  // A preset's upper bound is the end of its minute; the panel has nothing to say about the future.
  const end = new Date(
    Math.min(bounds.end?.getTime() ?? now.getTime(), now.getTime()),
  );
  const floor = end.getTime() - ACTIVITY_WINDOW_MAX_MS;
  const wanted = bounds.start?.getTime() ?? floor;
  return {
    start: new Date(Math.max(wanted, floor)),
    end,
    truncated: wanted < floor,
  };
}

function createdBounds(condition: Condition | undefined): {
  start?: Date;
  end?: Date;
} {
  if (!condition) {
    return {};
  }
  if ("conditions" in condition) {
    return condition.conditions.reduce<{ start?: Date; end?: Date }>(
      (acc, inner) => ({ ...acc, ...createdBounds(inner) }),
      {},
    );
  }
  if (condition.field !== CREATED || typeof condition.value !== "string") {
    return {};
  }
  const at = new Date(condition.value);
  switch (condition.comparator) {
    case "GREATER_THAN":
    case "GREATER_THAN_OR_EQUAL":
      return { start: at };
    case "LESS_THAN":
    case "LESS_THAN_OR_EQUAL":
      return { end: at };
    default:
      return {};
  }
}
